<script setup lang="ts">
/**
 * Compact pager. Always shows prev/next + the current/total label;
 * the explicit page-number row only renders when there's room
 * (desktop), to keep mobile minimal.
 */
import { computed } from 'vue'
import { ChevronLeft, ChevronRight } from '@lucide/vue'

import { AppButton } from '@/shared/ui'

interface Props {
  page: number
  totalPages: number
  /** Cap on inline numeric buttons (desktop). 0 to hide them entirely. */
  windowSize?: number
}

const props = withDefaults(defineProps<Props>(), { windowSize: 5 })
const emit = defineEmits<{ 'update:page': [page: number] }>()

const hasPrev = computed(() => props.page > 1)
const hasNext = computed(() => props.page < props.totalPages)

const pages = computed<number[]>(() => {
  if (props.windowSize <= 0 || props.totalPages <= 1) return []
  const w = Math.min(props.windowSize, props.totalPages)
  let start = Math.max(1, props.page - Math.floor(w / 2))
  const end = Math.min(props.totalPages, start + w - 1)
  start = Math.max(1, end - w + 1)
  const out: number[] = []
  for (let i = start; i <= end; i += 1) out.push(i)
  return out
})

function go(p: number) {
  if (p < 1 || p > props.totalPages || p === props.page) return
  emit('update:page', p)
}
</script>

<template>
  <nav
    v-if="totalPages > 1"
    class="flex items-center justify-between gap-2"
    aria-label="Pagination"
  >
    <AppButton
      variant="outline"
      size="sm"
      :disabled="!hasPrev"
      aria-label="Previous page"
      @click="go(page - 1)"
    >
      <ChevronLeft class="size-4" aria-hidden="true" />
      <span class="hidden sm:inline">Previous</span>
    </AppButton>

    <div class="hidden items-center gap-1 md:flex">
      <AppButton
        v-for="p in pages"
        :key="p"
        :variant="p === page ? 'primary' : 'ghost'"
        size="sm"
        :aria-current="p === page ? 'page' : undefined"
        :aria-label="`Page ${p}`"
        @click="go(p)"
      >
        {{ p }}
      </AppButton>
    </div>

    <p class="text-sm text-muted-foreground md:hidden">Page {{ page }} of {{ totalPages }}</p>

    <AppButton
      variant="outline"
      size="sm"
      :disabled="!hasNext"
      aria-label="Next page"
      @click="go(page + 1)"
    >
      <span class="hidden sm:inline">Next</span>
      <ChevronRight class="size-4" aria-hidden="true" />
    </AppButton>
  </nav>
</template>
